import React, { useState, useRef, useCallback } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Modal,
  Typography,
  Button,
  Form,
  Select,
  Checkbox,
  Radio,
  useToast,
  Alert,
  AlertDescription,
} from '@crisellevaldez/speculo-ui';
import { Upload, FileText, X, PlusCircle } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { TagInput } from '../../common/TagInput';
import { TipBox } from '../onboarding/components/TipBox';
import {
  databaseUploadSchema,
  DatabaseUploadFormData,
} from '../../../schemas/databaseUpload.schema';
import { useDatabaseUpload } from '../../../hooks/useDatabaseUpload';
import { useOrganizationUsers } from '../../../hooks/useOrganizationUsers';
import { useOrganizationStore } from '../../../stores/useOrganizationStore';
import { AddTeamMemberModal } from './AddTeamMemberModal';

const FLOW_TYPE_OPTIONS = [
  { value: 'engage', label: 'Engage' },
  { value: 'revive', label: 'Revive' },
  { value: 'e-alerts', label: 'E-Alerts' },
];

const ACCEPTED_FILE_TYPES = '.csv,.xlsx,.xls';

interface UploadDatabaseModalProps {
  isOpen: boolean;
  onClose: () => void;
}

/**
 * UploadDatabaseModal component for uploading database contacts
 * This modal is only accessible to non-FUB users
 *
 * @component
 */
export const UploadDatabaseModal: React.FC<UploadDatabaseModalProps> = ({ isOpen, onClose }) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const { submitDatabaseUpload, isSubmitting, isSubmitted, resetSubmission } = useDatabaseUpload();
  const { users, loading: loadingTeamMembers, addTeamMember } = useOrganizationUsers();
  const { organization } = useOrganizationStore();
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const [showAddTeamMember, setShowAddTeamMember] = useState(false);
  const [isAddingTeamMember, setIsAddingTeamMember] = useState(false);

  const {
    control,
    handleSubmit,
    watch,
    setValue,
    formState: { errors, isValid },
    reset,
  } = useForm<DatabaseUploadFormData>({
    resolver: zodResolver(databaseUploadSchema),
    mode: 'onChange',
    defaultValues: {
      aiOptedIn: undefined,
      flowTypes: [],
      tags: [],
      assignedTo: null,
      acknowledgeOptIn: false,
      useAssignment: false,
      assignmentAction: '',
      assignedAgents: [],
      additionalNotes: '',
    },
  });

  const aiOptedIn = watch('aiOptedIn');

  // Handle tag changes
  const handleTagsChange = useCallback(
    (newTags: string[]) => {
      setValue('tags', newTags);
    },
    [setValue]
  );

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setSelectedFile(file);
    }
  };

  const handleRemoveFile = () => {
    setSelectedFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const onSubmit = async (data: DatabaseUploadFormData) => {
    if (!selectedFile) {
      return;
    }

    try {
      await submitDatabaseUpload(data, selectedFile);
    } catch (error) {
      // Error is handled in the hook
    }
  };

  const handleClose = () => {
    reset();
    setSelectedFile(null);
    resetSubmission();
    onClose();
  };

  const isFormComplete = isValid && selectedFile !== null;

  // Show success message after submission
  if (isSubmitted) {
    return (
      <Modal isOpen={isOpen} onClose={handleClose} size="sm">
        <Modal.Body>
          <div className="text-center">
            <div className="py-4 items-center gap-6">
              <div>
                <Typography variant="h2" className="text-2xl font-bold mb-4">
                  Database Loading! 🤖
                </Typography>
                <Typography variant="p" className="text-gray-600 dark:text-gray-400 max-w-md mx-auto">
                  Our team is busy getting all your contacts securely tucked into your account and
                  ready for launch flows. Keep an eye on your inbox—we'll let you know the second
                  the upload is complete!
                </Typography>
              </div>
              <Button variant="primary" onClick={handleClose} className="mt-4">
                Close
              </Button>
            </div>
          </div>
        </Modal.Body>
      </Modal>
    );
  }

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="lg">
      <Modal.Header>
        <Typography variant="h3">Upload Database</Typography>
        <Typography variant="small" className="text-gray-600 dark:text-gray-400">
          Your database needs to be uploaded to your account by speculo.ai team.
        </Typography>
      </Modal.Header>
      <Modal.Body>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
          {/* AI-Call Opted-in question */}
          <Form.Control>
            <Form.Label>
              AI-Call Opted-in Contacts? <span className="text-red-500">*</span>
            </Form.Label>
            <Controller
              name="aiOptedIn"
              control={control}
              render={({ field }) => (
                <div className="flex gap-4">
                  <Button
                    type="button"
                    variant={field.value === 'yes' ? 'primary' : 'outline'}
                    onClick={() => {
                      field.onChange('yes');
                      setValue('acknowledgeOptIn', false);
                    }}
                    className="flex-1"
                  >
                    Yes
                  </Button>
                  <Button
                    type="button"
                    variant={field.value === 'no' ? 'primary' : 'outline'}
                    onClick={() => {
                      field.onChange('no');
                      setValue('flowTypes', []);
                      setValue('acknowledgeOptIn', false);
                    }}
                    className="flex-1"
                  >
                    No
                  </Button>
                </div>
              )}
            />
            {errors.aiOptedIn && <Form.ErrorMessage>{errors.aiOptedIn.message}</Form.ErrorMessage>}
          </Form.Control>

          {/* Flow Type - Only show if YES */}
          {aiOptedIn === 'yes' && (
            <Form.Control>
              <Form.Label>
                Flow Type(s) <span className="text-red-500">*</span>
              </Form.Label>
              <Controller
                name="flowTypes"
                control={control}
                render={({ field }) => (
                  <Select
                    placeholder="Select Flow Type(s)"
                    value={field.value || []}
                    onChange={(value) => {
                      if (Array.isArray(value)) {
                        field.onChange(value);
                      }
                    }}
                    options={FLOW_TYPE_OPTIONS}
                    multiple={true}
                  />
                )}
              />
              {errors.flowTypes && (
                <Form.ErrorMessage>{errors.flowTypes.message}</Form.ErrorMessage>
              )}
            </Form.Control>
          )}

          {/* Tags */}
          <Form.Control>
            <Form.Label>Tag(s)</Form.Label>
            <TagInput
              tags={watch('tags') || []}
              onTagsChange={handleTagsChange}
              placeholder="Type a tag and press Enter"
            />
            <TipBox>
              These tags will be applied to contacts and can be used for filtering in flows.
            </TipBox>
          </Form.Control>

          {/* Agent Assignment */}
          <div className="border border-gray-200 dark:border-white/10 rounded-lg p-4 space-y-4">
            <Form.Control>
              <Form.Label>
                Would you like the contacts to be assigned to specific agent(s) or should contacts
                remain unassigned?
              </Form.Label>
              <Controller
                name="assignmentAction"
                control={control}
                render={({ field }) => (
                  <div className="flex gap-4 mt-2">
                    <Radio
                      id="assignmentAction-assign"
                      value="assign"
                      checked={field.value === 'assign'}
                      onChange={() => {
                        field.onChange('assign');
                        setValue('useAssignment', true);
                      }}
                      label="Assign"
                    />
                    <Radio
                      id="assignmentAction-unassigned"
                      value="unassigned"
                      checked={field.value === 'unassigned'}
                      onChange={() => {
                        field.onChange('unassigned');
                        setValue('useAssignment', true);
                        // Clear assigned agents when selecting "Unassigned" option
                        setValue('assignedAgents', []);
                      }}
                      label="Unassigned"
                    />
                  </div>
                )}
              />
            </Form.Control>

            {/* Only show agent selector when "Assign" is selected, not for "Unassigned" */}
            {watch('assignmentAction') === 'assign' && (
              <Form.Control>
                <Form.Label>Agent Name(s):</Form.Label>
                <div className="flex gap-2">
                  <div className="flex-1">
                    <Controller
                      name="assignedAgents"
                      control={control}
                      render={({ field }) => (
                        <Select
                          isLoading={loadingTeamMembers}
                          options={users}
                          rows={4}
                          value={field.value || []}
                          onChange={(value) => {
                            if (Array.isArray(value)) {
                              field.onChange(value);
                            }
                          }}
                          placeholder="Select team members"
                          multiple={true}
                          searchable
                        />
                      )}
                    />
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    className="whitespace-nowrap h-9"
                    onClick={() => setShowAddTeamMember(true)}
                  >
                    <PlusCircle className="w-4 h-4 mr-2" />
                    Add New Team
                  </Button>
                </div>
              </Form.Control>
            )}
          </div>

          <AddTeamMemberModal
            isOpen={showAddTeamMember}
            onClose={() => setShowAddTeamMember(false)}
            onSubmit={async (data) => {
              setIsAddingTeamMember(true);
              try {
                if (!organization?.id) {
                  throw new Error('No organization ID');
                }
                await addTeamMember(organization.id, data);
                await queryClient.invalidateQueries({ queryKey: ['teamMembers'] });
                setShowAddTeamMember(false);
                showToast({
                  message: 'Team member added successfully',
                  variant: 'success',
                });
              } catch (error) {
                showToast({
                  message: error instanceof Error ? error.message : 'Error adding team member',
                  variant: 'error',
                });
              } finally {
                setIsAddingTeamMember(false);
              }
            }}
            isSubmitting={isAddingTeamMember}
          />

          {/* Additional Notes */}
          <Form.Control>
            <Form.Label>Any other notes we should know about this upload?</Form.Label>
            <Controller
              name="additionalNotes"
              control={control}
              render={({ field }) => (
                <textarea
                  className="shadow-sm w-full border rounded-md border-gray-300 dark:border-white/10 py-2 px-3 text-gray-900 dark:text-gray-100 text-sm placeholder-gray-500 dark:placeholder-gray-400 placeholder:text-sm focus:outline-none focus:ring-1 focus:ring-gray-400 disabled:cursor-not-allowed disabled:bg-gray-50 disabled:text-gray-500 bg-white dark:bg-transparent min-h-[100px] resize-y"
                  placeholder="Enter any additional notes or special requirements for this database upload..."
                  value={field.value || ''}
                  onChange={field.onChange}
                />
              )}
            />
          </Form.Control>

          {/* File Upload */}
          <Form.Control>
            <Form.Label>
              Upload Database File <span className="text-red-500">*</span>
            </Form.Label>
            <input
              ref={fileInputRef}
              type="file"
              accept={ACCEPTED_FILE_TYPES}
              onChange={handleFileSelect}
              className="hidden"
            />
            {selectedFile ? (
              <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-white/5 border border-gray-200 dark:border-white/10 rounded-lg">
                <div className="flex items-center gap-3">
                  <FileText className="w-8 h-8 text-blue-600" />
                  <div>
                    <Typography variant="p" className="font-medium">
                      {selectedFile.name}
                    </Typography>
                    <Typography variant="small" className="text-gray-500 dark:text-gray-400">
                      {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                    </Typography>
                  </div>
                </div>
                <Button type="button" variant="outline" onClick={handleRemoveFile} className="p-2">
                  <X className="w-4 h-4" />
                </Button>
              </div>
            ) : (
              <Button
                type="button"
                variant="outline"
                onClick={() => fileInputRef.current?.click()}
                className="w-full py-6 border-dashed flex flex-col items-center gap-2"
              >
                <Upload className="w-6 h-6 text-gray-400" />
                <div className="text-center">
                  <Typography variant="p" className="font-medium text-sm">
                    Click to upload your database file
                  </Typography>
                  <Typography variant="small" className="text-gray-500">
                    CSV, XLSX, XLS
                  </Typography>
                </div>
              </Button>
            )}
          </Form.Control>

          {/* Data Requirements Info Box */}
          <Alert variant="info">
            <AlertDescription>
              <p className="font-medium mb-2">
                Data to be included in your file:
              </p>
              <ul className="text-sm space-y-1">
                <li>• First Name</li>
                <li>• Last Name (if known)</li>
                <li>• Phone (if known)</li>
                <li>• Email (if known)</li>
                <li>• Address (if known)</li>
                <li>• Source</li>
                <li>• Assigned Agent (if needed)</li>
              </ul>
            </AlertDescription>
          </Alert>

          {/* Acknowledgment Checkbox - Only show if YES */}
          {aiOptedIn === 'yes' && (
            <Alert variant="warning">
              <AlertDescription className="w-full">
                <Controller
                  name="acknowledgeOptIn"
                  control={control}
                  render={({ field }) => (
                    <label className="flex items-start gap-3 cursor-pointer">
                      <Checkbox
                        checked={field.value || false}
                        onChange={(e) => field.onChange(e.target.checked)}
                        className="mt-0.5"
                      />
                      <span className="text-sm">
                        By checking this box and clicking SUBMIT, you acknowledge and agree that all
                        of the leads in the database being provided have opted in to be called by an
                        AI voice bot and/or robo dialer.
                      </span>
                    </label>
                  )}
                />
                {errors.acknowledgeOptIn && (
                  <Form.ErrorMessage className="mt-2">
                    {errors.acknowledgeOptIn.message}
                  </Form.ErrorMessage>
                )}
              </AlertDescription>
            </Alert>
          )}
        </form>
      </Modal.Body>
      <Modal.Footer>
        <div className="flex justify-end gap-3">
          <Button
            variant="primary"
            onClick={handleSubmit(onSubmit)}
            disabled={!isFormComplete || isSubmitting}
            isLoading={isSubmitting}
            className="flex items-center gap-2 h-8"
          >
            <Upload size={18} />
            Submit
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
};
